import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    QidModel,
    QidProduct,
} from "server/models"
import {
    StoreType,
    ResultDatapointListType,
    CalculateQidResultProps,
    CalculateQidResultData,
    setSilModelDevicesQidExpanded,
} from "client/store"
import {
    TableRow,
    TableCell,
    TableCellContentWrapper,
    TableCellContent,
    SpinnerIcon,
} from "client/components"

import { RowCollapseControl } from "client/pages/qid/product/models/list/styled"

import { ColumnsType, SelectDeviceType, SelectDevice } from "../columns"
import ModelRows from "./modelRows"

export const paddingStyle: React.CSSProperties = { paddingLeft: "32px" }

const Rows = (columns: ColumnsType) => {
    const productList = useSelector((state: StoreType) => state.silModelDevices.qidProducts.productList)
    const productIds = useSelector((state: StoreType) => state.silModelDevices.qidProducts.productIds)
    const datapointList = useSelector((state: StoreType) => state.silModelDevices.qidProducts.datapointList)
    const calculationMethod = useSelector((state: StoreType) => state.silModelDevices.qidProducts.calculationMethod)
    const expanded = useSelector((state: StoreType) => state.silModelDevices.qidProducts.expanded)
    const modelTarget = useSelector((state: StoreType) => state.silModelDevices.modelTarget)
    const onSelect = useSelector((state: StoreType) => state.silModelDevices.onSelect)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const dispatch = useDispatch()

    const toggleProduct = (productId: number) => {
        const expandedProducts = expanded.productIds.slice()
        if (expandedProducts.includes(productId)) {
            dispatch(setSilModelDevicesQidExpanded({
                productIds: expandedProducts.filter((id) => id !== productId),
            }))
        } else {
            expandedProducts.push(productId)
            dispatch(setSilModelDevicesQidExpanded({ productIds: expandedProducts }))
        }
    }

    let rows: React.ReactNode[] = []

    productIds.forEach((productId) => {
        const product = productList[productId]
        if (!product) return

        const productDatapointList = datapointList[productId] as ResultDatapointListType || null
        const baseCalculationProps: CalculateQidResultProps = {
            models: product.models as QidModel[],
            datapointList: productDatapointList,
            method: calculationMethod,
            modelId: null,
            safetyFunctionId: null,
            optionId: null,
            datapointId: null,
        }

        const productAvg = productDatapointList ? CalculateQidResultData(baseCalculationProps) : null

        const selectProductProps: SelectDeviceType = {
            product,
            model: null,
            safetyFunction: null,
            option: null,
            datapoint: null,
            dataAvg: productAvg,
            method: calculationMethod,

            project,
            modelTarget,
            onSelect,
        }

        const productExpanded = expanded.productIds.includes(productId)

        rows.push(
            <TableRow
                key={`product_${product.id}`}
                size="md"
                onClick={() => SelectDevice(selectProductProps)}
            >
                {
                    columns.productColumns
                    .concat(columns.commonColumns)
                    .map((column, index) => (
                        <TableCell
                            key={column.field}
                            cellWidth={column.cellWidth}
                            cellMinWidth={column.cellMinWidth}
                            cellMaxWidth={column.cellMaxWidth}
                            centerAlign={column.centerAlign}
                        >
                            <TableCellContentWrapper style={index === 0 ? paddingStyle : null}>
                                <TableCellContent>
                                    {
                                        index === 0 && (
                                            <RowCollapseControl
                                                collapsed={!productExpanded}
                                                onClick={(e) => {
                                                    // don't show nested rows until product datapoints are loaded
                                                    e.stopPropagation()
                                                    return productDatapointList ? toggleProduct(product.id) : null
                                                }}
                                            />
                                        )
                                    }
                                    {
                                        column.formatter
                                            ? column.formatter(product)
                                            : product[column.field as keyof QidProduct]
                                    }
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                    ))
                }
                {
                    columns.resultColumns.map((column) => (
                        <TableCell
                            key={column.field}
                            cellWidth={column.cellWidth}
                            cellMinWidth={column.cellMinWidth}
                            cellMaxWidth={column.cellMaxWidth}
                        >
                            <TableCellContentWrapper>
                                <TableCellContent>
                                    {
                                        productAvg
                                            ? column.formatter(productAvg)
                                            : <span style={{ fontSize: "7px" }}><SpinnerIcon /></span>
                                    }
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                    ))
                }
            </TableRow>,
        )

        if (productExpanded) {
            const modelRows = ModelRows(
                columns,
                product.models,
                expanded,
                productDatapointList,
                product.id,
                baseCalculationProps,
                selectProductProps,
                dispatch,
            )
            rows = rows.concat(modelRows)
        }
    })

    return rows
}
export default Rows
